import Honeybadger from "@honeybadger-io/js"
import { removeRetryBanner, showRetryBanner } from "../lib/retry_banner"

// Render has no zero-downtime deploys: services with a persistent disk (sqlite)
// restart in place, so the app answers 502 for a stretch of every deploy —
// nightly, in production. setup/turbo_frame_retry already rides that out for
// turbo-frames. This covers the other two ways Turbo talks to the server.
//
// Nothing here helps a cold first visit; the page has to already be running.
const RETRIABLE_STATUSES = [502, 503, 504]

// ~3 minutes of cover, front-loaded because the user is watching a stalled
// page. The 2026-09-07 deploy served 502s for roughly that long.
//
// ponytail: a fixed ladder, no jitter. One browser tab retrying is not a
// thundering herd. Add jitter if this ever runs somewhere with real fan-out.
const RETRY_DELAYS = [1_000, 3_000, 8_000, 20_000, 45_000, 60_000, 60_000]

const RETRYING_MESSAGE = "Temporary outage. Retrying&hellip;"
const FORM_FAILED_MESSAGE = "We couldn't reach the server, so your changes weren't saved. Please try again in a moment."

// Page visits: retried underneath Turbo, in a fetch wrapper, rather than by
// cancelling the response it is waiting on.
//
// The obvious approach — preventDefault() on turbo:before-fetch-response and
// re-issue the visit — does not work here. Visit#requestPreventedHandlingResponse
// is an empty method, so a prevented response strands the Visit: it never
// completes, Session#currentVisit stays set, and every later
// turbo_stream.refresh silently stops working. That is the same trap
// visit.cancel() sets. Retrying below Turbo means Turbo's state machine never
// learns a 502 happened; the visit just takes longer.
//
// Turbo resolves window.fetch at call time (fetchWithTurboHeaders), so
// replacing it here is enough, and it must happen before the first navigation.
const nativeFetch = window.fetch.bind(window)

window.fetch = async (input, init = {}) => {
  let response = await nativeFetch(input, init)
  if (!isRetriableVisit(init, response)) return response

  for (const delay of RETRY_DELAYS) {
    showRetryBanner(RETRYING_MESSAGE)
    await sleep(delay, init.signal)

    response = await nativeFetch(input, init)
    if (!RETRIABLE_STATUSES.includes(response.status)) {
      removeRetryBanner()
      return response
    }
  }

  // Out of retries: hand Turbo the 502 and let it render, which is what would
  // have happened immediately without any of this.
  notifyHoneybadger("Turbo visit still failing after retries", {url: String(input), status: response.status})
  return response
}

// Only Turbo's own GETs, and only the ones turbo_frame_retry does not own.
// X-Turbo-Request-Id is on every Turbo request (and nothing else), so a Stripe
// or Honeybadger call is never touched.
function isRetriableVisit(init, response) {
  if (!RETRIABLE_STATUSES.includes(response.status)) return false

  const headers = init.headers
  if (!(headers instanceof Headers) || !headers.has("X-Turbo-Request-Id")) return false
  if (headers.has("Turbo-Frame")) return false

  // Never a write. A 502 during a restart probably means the request never
  // reached Rails — but "probably" is not good enough to re-send a POST, and a
  // double-submitted form is worse than an error page. Those are handled below.
  return (init.method || "GET").toUpperCase() === "GET"
}

// Aborts (the user navigating away mid-retry) reject, which stops the ladder.
// Turbo already treats AbortError as a non-error, and setup/handle_network_errors
// keeps it out of Honeybadger.
function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError())

    const timer = setTimeout(resolve, ms)
    signal?.addEventListener("abort", () => {
      clearTimeout(timer)
      reject(abortError())
    }, {once: true})
  })
}

function abortError() {
  return new DOMException("Fetch is aborted", "AbortError")
}

// Form submissions: say so, don't retry.
//
// preventDefault() is safe here in a way it is not for a visit —
// FormSubmission#requestPreventedHandlingResponse just records the result — and
// it is what stops Turbo replacing the page the user was filling in with a 502
// error page, losing everything they typed.
document.addEventListener("turbo:before-fetch-response", event => {
  if (!(event.target instanceof HTMLFormElement)) return
  if (!RETRIABLE_STATUSES.includes(event.detail.fetchResponse.statusCode)) return

  event.preventDefault()
  showRetryBanner(FORM_FAILED_MESSAGE, {final: true})
})

document.addEventListener("turbo:load", () => removeRetryBanner())

function notifyHoneybadger(message, context) {
  Honeybadger.notify({message, name: "turbo:retry-exhausted", context})
}
